'use client';

import { Button } from '@petsmart-ui/sparky';
import { useEffect, useRef, useState } from 'react';

import { VariantOption } from '../product-details';
import type { MiniPdpVariantGroup } from './model/use-mini-pdp-variants';

const SCROLL_EPSILON = 0.5;

type MiniPdpVariantRowProps = Readonly<{
    group: MiniPdpVariantGroup;
    onChange: (value: string) => void;
}>;

const getClusterPositions = (row: HTMLDivElement) => {
    const maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
    const options = Array.from(row.children).map(option => option as HTMLElement);
    const positions = [0];
    let position = 0;

    while (position < maxScrollLeft - SCROLL_EPSILON) {
        const nextOption = options.find(
            option => option.offsetLeft + option.offsetWidth > position + row.clientWidth + SCROLL_EPSILON
        );
        const nextPosition = nextOption ? Math.min(Math.max(0, nextOption.offsetLeft), maxScrollLeft) : maxScrollLeft;

        if (nextPosition <= position + SCROLL_EPSILON) {
            positions.push(maxScrollLeft);
            break;
        }

        positions.push(nextPosition);
        position = nextPosition;
    }

    return positions;
};

export function MiniPdpVariantRow({ group, onChange }: MiniPdpVariantRowProps) {
    const rowRef = useRef<HTMLDivElement>(null);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    useEffect(() => {
        const row = rowRef.current;
        if (!row) return;

        const syncScrollPosition = () => {
            const maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
            setCanScrollPrevious(row.scrollLeft > SCROLL_EPSILON);
            setCanScrollNext(row.scrollLeft < maxScrollLeft - SCROLL_EPSILON);
        };

        row.addEventListener('scroll', syncScrollPosition, { passive: true });
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(syncScrollPosition);
        resizeObserver?.observe(row);

        syncScrollPosition();

        return () => {
            row.removeEventListener('scroll', syncScrollPosition);
            resizeObserver?.disconnect();
        };
    }, [group.variants.length]);

    const scrollTo = (target: number) => {
        const row = rowRef.current;
        if (!row) return;

        if (typeof row.scrollTo === 'function') {
            row.scrollTo({ left: target, behavior: 'smooth' });
        } else {
            row.scrollLeft = target;
        }
    };

    const scrollToNextOption = () => {
        const row = rowRef.current;
        if (!row) return;

        const target = getClusterPositions(row).find(position => position > row.scrollLeft + SCROLL_EPSILON);

        if (target === undefined) return;

        scrollTo(target);
    };

    const scrollToPreviousOption = () => {
        const row = rowRef.current;
        if (!row) return;

        const target = getClusterPositions(row).findLast(position => position < row.scrollLeft - SCROLL_EPSILON);

        if (target === undefined) return;

        scrollTo(target);
    };

    return (
        <div className="mini-pdp-drawer__variant-row-container">
            <div
                ref={rowRef}
                className="mini-pdp-drawer__variant-row"
                data-testid={`mini-pdp-variant-row-${group.name}`}
            >
                {group.variants.map(variant => (
                    <VariantOption
                        key={variant.value}
                        value={variant.value}
                        display={variant.display}
                        variantType={group.name}
                        isChecked={variant.isChecked}
                        isUnavailable={variant.isUnavailable}
                        handleChangeAction={() => onChange(variant.value)}
                    />
                ))}
            </div>
            {canScrollPrevious && (
                <Button
                    circle
                    type="button"
                    text="Previous options"
                    aria-label={`Previous ${group.name} options`}
                    iconName="chevron-left"
                    iconSize="sm"
                    hideText
                    variant="primary"
                    className="mini-pdp-drawer__variant-scroll-button mini-pdp-drawer__variant-scroll-button--previous"
                    data-testid={`mini-pdp-variant-previous-${group.name}`}
                    onClick={scrollToPreviousOption}
                />
            )}
            {canScrollNext && (
                <Button
                    circle
                    type="button"
                    text="Next options"
                    aria-label={`Next ${group.name} options`}
                    iconName="chevron-right"
                    iconSize="sm"
                    hideText
                    variant="primary"
                    className="mini-pdp-drawer__variant-scroll-button mini-pdp-drawer__variant-scroll-button--next"
                    data-testid={`mini-pdp-variant-next-${group.name}`}
                    onClick={scrollToNextOption}
                />
            )}
        </div>
    );
}
